import React, { useState, useEffect } from 'react';
import { ClipboardList, Trash2, MapPin, Car, Map as MapIcon, CheckCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMapsLibrary, Map, AdvancedMarker, Pin, InfoWindow } from '@vis.gl/react-google-maps';

export default function Tasks() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const navigate = useNavigate();
  const routesLibrary = useMapsLibrary('routes');

  const [savedPlaces, setSavedPlaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [directionsService, setDirectionsService] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(false);
  const [distanceError, setDistanceError] = useState(false);
  const [travelTimes, setTravelTimes] = useState({});
  const [activePin, setActivePin] = useState(null);

  useEffect(() => {
    fetchSavedPlaces();
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setLocationError(false);
        },
        (err) => {
          console.error("Geolocation error:", err);
          setLocationError(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setLocationError(true);
    }
  }, []);

  const useCompanyLocation = () => {
    // TMHWST HQ coordinates roughly Bangna
    setUserLocation({ lat: 13.6738, lng: 100.6322 });
    setLocationError(false);
  };

  useEffect(() => {
    if (routesLibrary) {
      setDirectionsService(new routesLibrary.DirectionsService());
    }
  }, [routesLibrary]);

  useEffect(() => {
    const plannedTasks = savedPlaces.filter(p => p.is_planned == 1 && p.is_visited != 1);
    if (plannedTasks.length === 0 || !userLocation || !directionsService || !window.google) return;

    const origin = new window.google.maps.LatLng(userLocation.lat, userLocation.lng);
    
    const fetchAllRoutes = async () => {
      const newTravelTimes = {};
      let hasError = false;

      // Calculate route for each task individually
      for (const task of plannedTasks) {
        try {
          const destination = new window.google.maps.LatLng(parseFloat(task.lat), parseFloat(task.lng));
          const response = await directionsService.route({
            origin: origin,
            destination: destination,
            travelMode: window.google.maps.TravelMode.DRIVING
          });

          if (response.routes && response.routes[0] && response.routes[0].legs[0]) {
            const leg = response.routes[0].legs[0];
            newTravelTimes[task.id] = {
              distance: leg.distance.text,
              duration: leg.duration.text
            };
          } else {
            hasError = true;
          }
        } catch (err) {
          console.error("Directions Error for task", task.id, err);
          hasError = true;
        }
      }

      setTravelTimes(newTravelTimes);
      if (hasError && Object.keys(newTravelTimes).length === 0) {
        setDistanceError(true);
      } else {
        setDistanceError(false);
      }
    };

    fetchAllRoutes();
  }, [savedPlaces, userLocation, directionsService]);

  const fetchSavedPlaces = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/google_places?email=${user?.google_email || ''}`);
      const data = await res.json();
      if (data.status === 'success') {
        setSavedPlaces(data.data);
      }
    } catch (err) {
      console.error("Error fetching saved places", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePlace = async (id) => {
    if (!window.confirm("คุณต้องการลบข้อมูลนี้ใช่หรือไม่?")) return;
    try {
      const res = await fetch('/api/google_places/' + id, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchSavedPlaces();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdatePlace = async (id, action) => {
    try {
      const res = await fetch('/api/google_places/' + id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, action })
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchSavedPlaces();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleShowOnMap = (place) => {
    navigate('/', { 
      state: { 
        searchResult: { 
          type: 'google', 
          data: { place_id: place.place_id } 
        } 
      } 
    });
  };

  const plannedTasks = savedPlaces.filter(p => p.is_planned == 1 && p.is_visited != 1);
  const visitedTasks = savedPlaces.filter(p => p.is_visited == 1 && p.is_potential != 1);
  const potentialTasks = savedPlaces.filter(p => p.is_visited == 1 && p.is_potential == 1);
  
  // Combine for map bounds
  const mapTasks = [...plannedTasks, ...visitedTasks, ...potentialTasks];
  const centerLat = mapTasks.length > 0 ? parseFloat(mapTasks[0].lat) : (userLocation ? userLocation.lat : 13.7563);
  const centerLng = mapTasks.length > 0 ? parseFloat(mapTasks[0].lng) : (userLocation ? userLocation.lng : 100.5018);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.8rem', color: '#1f2937' }}>งานของฉัน (My Tasks)</h1>
      </div>

      {/* Map Section */}
      <div className="card" style={{ height: '400px', width: '100%', borderRadius: '12px', overflow: 'hidden', padding: 0 }}>
        <Map
          defaultCenter={{ lat: centerLat, lng: centerLng }}
          defaultZoom={mapTasks.length > 0 ? 10 : 6}
          mapId="tasks_map_id"
          gestureHandling={'greedy'}
          disableDefaultUI={false}
        >
          {mapTasks.map((task) => {
            const isVisited = task.is_visited == 1;
            return (
              <AdvancedMarker
                key={task.id}
                position={{ lat: parseFloat(task.lat), lng: parseFloat(task.lng) }}
                onClick={() => setActivePin(task)}
              >
                <Pin background={isVisited ? '#22c55e' : '#ef4444'} borderColor={isVisited ? '#16a34a' : '#b91c1c'} glyphColor="#ffffff" />
              </AdvancedMarker>
            );
          })}
          
          {activePin && (
            <InfoWindow
              position={{ lat: parseFloat(activePin.lat), lng: parseFloat(activePin.lng) }}
              onCloseClick={() => setActivePin(null)}
            >
              <div style={{ padding: '5px', maxWidth: '200px' }}>
                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#1e293b' }}>{activePin.name}</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                  สถานะ: <strong style={{ color: activePin.is_visited == 1 ? '#22c55e' : '#ef4444' }}>
                    {activePin.is_visited == 1 ? 'เยี่ยมแล้ว' : 'วางแผนจะไป'}
                  </strong>
                </p>
              </div>
            </InfoWindow>
          )}
        </Map>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
        {/* Planned Tasks */}
        <div className="card" style={{ padding: '1.5rem', backgroundColor: '#fff1f2', borderLeft: '4px solid #ef4444', minHeight: '40vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <ClipboardList color="#ef4444" />
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 700 }}>รายการที่วางแผนไปเยี่ยม ({plannedTasks.length})</h3>
          </div>
          
          {plannedTasks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem 0' }}>ไม่มีรายการ</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {plannedTasks.map(p => (
                <div key={p.id} style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '1rem', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1rem' }}>{p.name}</h4>
                    <button onClick={() => handleDeletePlace(p.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }} title="ลบรายการ">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{p.address}</span>
                  </p>
                  
                  {travelTimes[p.id] ? (
                    <div style={{ backgroundColor: '#eff6ff', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Car size={16} />
                      <span>ใช้เวลาเดินทาง <strong>{travelTimes[p.id].duration}</strong> ({travelTimes[p.id].distance})</span>
                    </div>
                  ) : distanceError ? (
                    <div style={{ backgroundColor: '#fef2f2', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <MapPin size={16} />
                      <span>ไม่สามารถคำนวณเส้นทางได้ (สถานที่อาจไม่ถูกต้อง)</span>
                    </div>
                  ) : locationError ? (
                    <div style={{ backgroundColor: '#fef2f2', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: '#ef4444', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MapPin size={16} />
                        <span>ไม่สามารถระบุตำแหน่ง GPS ของคุณได้</span>
                      </div>
                      <button onClick={useCompanyLocation} style={{ backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', alignSelf: 'flex-start' }}>
                        คำนวณจากที่ตั้งบริษัทแทน
                      </button>
                    </div>
                  ) : (
                    <div style={{ backgroundColor: '#f1f5f9', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #cbd5e1', borderTop: '2px solid #64748b', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        <span>{userLocation ? 'กำลังคำนวณระยะทาง...' : 'กำลังรอตำแหน่ง GPS...'}</span>
                      </div>
                      {!userLocation && (
                        <button onClick={useCompanyLocation} style={{ backgroundColor: 'white', color: '#64748b', border: '1px solid #cbd5e1', padding: '0.3rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', alignSelf: 'flex-start' }}>
                          ข้ามและใช้ที่ตั้งบริษัทแทน
                        </button>
                      )}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                    <button onClick={() => handleUpdatePlace(p.id, 'mark_visited')} style={{ flex: '1 1 100%', backgroundColor: '#22c55e', color: 'white', border: 'none', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                      <CheckCircle size={16} /> เช็คอิน (เยี่ยมแล้ว)
                    </button>
                    <button onClick={() => handleShowOnMap(p)} style={{ flex: 1, backgroundColor: 'white', color: '#3b82f6', border: '1px solid #bfdbfe', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 500 }}>
                      <MapIcon size={14} /> นำทาง
                    </button>
                    <button onClick={() => handleUpdatePlace(p.id, 'unplan_visit')} style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.8rem', fontWeight: 500 }}>
                      กลับไปรอดำเนินการ
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Visited Tasks */}
        <div className="card" style={{ padding: '1.5rem', backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e', minHeight: '40vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <CheckCircle color="#22c55e" />
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 700 }}>รายการที่เข้าเยี่ยมแล้ว ({visitedTasks.length})</h3>
          </div>
          
          {visitedTasks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem 0' }}>ไม่มีรายการ</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {visitedTasks.map(p => (
                <div key={p.id} style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '1rem', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1rem' }}>{p.name}</h4>
                    <button onClick={() => handleDeletePlace(p.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }} title="ลบรายการ">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{p.address}</span>
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 100%', backgroundColor: '#dcfce7', padding: '0.5rem', borderRadius: '6px', fontSize: '0.8rem', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 'bold' }}>
                      <CheckCircle size={16} /> เข้าเยี่ยมเรียบร้อยแล้ว
                    </div>
                    <button onClick={() => handleUpdatePlace(p.id, 'mark_potential')} style={{ flex: '1 1 100%', backgroundColor: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', padding: '0.5rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.9rem', fontWeight: 600 }}>
                      <Star size={16} /> มี Potential
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Potential Tasks */}
        <div className="card" style={{ padding: '1.5rem', backgroundColor: '#fffbeb', borderLeft: '4px solid #f59e0b', minHeight: '40vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <Star color="#f59e0b" fill="#f59e0b" />
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem', fontWeight: 700 }}>มี Potential (Follow up) ({potentialTasks.length})</h3>
          </div>
          
          {potentialTasks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem 0' }}>ไม่มีรายการ</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {potentialTasks.map(p => (
                <div key={p.id} style={{ border: '2px solid #fde68a', borderRadius: '8px', padding: '1rem', backgroundColor: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#b45309', fontSize: '1rem', fontWeight: 'bold' }}>{p.name}</h4>
                    <button onClick={() => handleDeletePlace(p.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }} title="ลบรายการ">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#64748b', display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
                    <MapPin size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{p.address}</span>
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #fef3c7', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 100%', backgroundColor: '#fef3c7', padding: '0.5rem', borderRadius: '6px', fontSize: '0.85rem', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontWeight: 'bold' }}>
                      <Star size={16} fill="#b45309" /> เข้าเยี่ยมแล้วและมี Potential
                    </div>
                    <button onClick={() => handleUpdatePlace(p.id, 'unmark_potential')} style={{ flex: '1 1 100%', backgroundColor: 'transparent', color: '#94a3b8', border: 'none', padding: '0.4rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', fontSize: '0.75rem', textDecoration: 'underline' }}>
                      ยกเลิก Potential
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
